import { useEffect, useState, useCallback } from 'react'
import { platformAPI } from '../services/api'

// All tenants, fetched once and shared by the sidebar badge, Today, the Tenants table
// and the command palette. Severity sorting and the filter-chip counts need the whole
// set, so the list is pulled in 100-row pages (the API max) rather than 20 at a time.

const TTL_MS = 60_000
const MAX_PAGES = 20

let cache = { tenants: null, loadedAt: 0, error: null }
let inflight = null
const listeners = new Set()
// Stable while loading, so consumers' effects keyed on `tenants` don't re-run every render.
const EMPTY = []

const emit = () => listeners.forEach((fn) => fn(cache))

const fetchAll = async () => {
  const all = []
  let page = 1
  let pages = 1
  do {
    const res = await platformAPI.getTenants({ page, per_page: 100 })
    const body = res.data || {}
    all.push(...(Array.isArray(body.data) ? body.data : []))
    pages = body.pagination?.pages || 1
    page += 1
  } while (page <= pages && page <= MAX_PAGES)
  return all
}

export const loadTenantDirectory = ({ force = false } = {}) => {
  const fresh = cache.tenants && Date.now() - cache.loadedAt < TTL_MS
  if (fresh && !force) return Promise.resolve(cache.tenants)
  if (inflight) return inflight
  inflight = fetchAll()
    .then((tenants) => {
      cache = { tenants, loadedAt: Date.now(), error: null }
      return tenants
    })
    .catch((err) => {
      cache = { ...cache, error: err }
      throw err
    })
    .finally(() => {
      inflight = null
      emit()
    })
  return inflight
}

export const invalidateTenantDirectory = () => {
  cache = { ...cache, loadedAt: 0 }
}

export const useTenantDirectory = () => {
  const [state, setState] = useState(cache)

  useEffect(() => {
    listeners.add(setState)
    loadTenantDirectory().catch(() => {})
    return () => listeners.delete(setState)
  }, [])

  const refresh = useCallback(() => loadTenantDirectory({ force: true }).catch(() => {}), [])

  return {
    tenants: state.tenants || EMPTY,
    loading: !state.tenants && !state.error,
    error: state.error,
    refresh,
  }
}
